/**
 * Descriptors dos tipos que ampliam o alcance do construtor:
 *
 *  - `treeview`        — dado hierárquico, que antes exigia componente escrito à mão;
 *  - `attributeSet`    — atributos de marketplace por MAPEAMENTO, substituindo os
 *                        cinco componentes que hoje resolvem o mesmo problema;
 *  - `customComponent` — o escape hatch, para o que não cabe em declaração.
 */
import { ScreenNode } from '../../model/node-props';
import { BUILTIN_ATTRIBUTE_PROFILES } from '../../model/attribute-schema';
import { ComponentDescriptor, defineDescriptor } from '../component-descriptor';
import { buildLayoutGroup } from './common-groups';
import { EnumOption } from '../property-schema';

/**
 * Onde um campo pode morar.
 *
 * `tab` entrou depois do relato "esta exigindo que sempre tenha um painel":
 * uma aba com um campo so nao precisa de painel nenhum, e obrigar a criar um
 * antes e a mesma friccao que fazia toda tela sair parecendo o mesmo cadastro.
 * O painel continua sendo o normal quando ha muitos campos.
 */
const FIELD_PARENTS = ['panel', 'row', 'section', 'tab'] as const;

/** Opções do seletor de perfil, montadas a partir dos perfis nativos. */
function buildProfileOptions(): readonly EnumOption[] {
  const options: EnumOption[] = [
    { value: '', labelKey: 'builderScreen.attributes.profile.custom' }
  ];
  for (const profile of BUILTIN_ATTRIBUTE_PROFILES) {
    options.push({ value: profile.id, labelKey: profile.labelKey });
  }
  return options;
}

/** O mapeamento manual só interessa quando não há perfil nativo escolhido. */
function isCustomProfile(node: ScreenNode): boolean {
  const profileId = (node.props as { profileId?: string }).profileId;
  return profileId === undefined || profileId === '';
}

// ---------------------------------------------------------------- treeview

export const treeViewDescriptor = defineDescriptor<'treeview'>({
  type: 'treeview',
  labelKey: 'builderScreen.palette.treeview',
  descriptionKey: 'builderScreen.palette.treeview.desc',
  icon: 'apartment',
  category: 'data',
  level: 'simple',

  createDefault: context => ({
    props: {
      childrenPath: 'children',
      labelPath: 'name',
      idPath: 'id',
      selectable: false,
      searchable: true,
      expandToLevel: 1
    },
    layout: { order: context.order, colSpan: 12, rowSpan: 4 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.groups.essential.title',
      level: 'simple',
      properties: [
        {
          kind: 'text',
          path: 'labelKey',
          labelKey: 'builderScreen.inspector.label',
          level: 'simple'
        },
        {
          kind: 'dataPath',
          path: 'dataPath',
          target: 'binding',
          labelKey: 'builderScreen.inspector.dataPath',
          helpKey: 'builderScreen.treeview.dataPathHelp',
          level: 'simple'
        }
      ]
    },
    {
      labelKey: 'builderScreen.treeview.structure',
      level: 'simple',
      properties: [
        {
          kind: 'text',
          path: 'labelPath',
          labelKey: 'builderScreen.treeview.labelPath',
          helpKey: 'builderScreen.treeview.labelPath.help',
          level: 'simple'
        },
        {
          kind: 'text',
          path: 'childrenPath',
          labelKey: 'builderScreen.treeview.childrenPath',
          helpKey: 'builderScreen.treeview.childrenPath.help',
          level: 'simple'
        },
        {
          kind: 'text',
          path: 'idPath',
          labelKey: 'builderScreen.treeview.idPath',
          level: 'advanced'
        },
        {
          kind: 'number',
          path: 'expandToLevel',
          labelKey: 'builderScreen.treeview.expandToLevel',
          helpKey: 'builderScreen.treeview.expandToLevel.help',
          level: 'simple',
          min: -1,
          max: 10
        }
      ]
    },
    {
      labelKey: 'builderScreen.groups.behavior',
      level: 'simple',
      properties: [
        {
          kind: 'boolean',
          path: 'searchable',
          labelKey: 'builderScreen.inspector.showSearch',
          level: 'simple'
        },
        {
          kind: 'boolean',
          path: 'selectable',
          labelKey: 'builderScreen.treeview.selectable',
          level: 'simple'
        },
        {
          kind: 'boolean',
          path: 'cascadeSelection',
          labelKey: 'builderScreen.treeview.cascadeSelection',
          helpKey: 'builderScreen.treeview.cascadeSelection.help',
          level: 'simple',
          // Só faz sentido se dá para marcar nó.
          visibleWhen: (node: ScreenNode) =>
            (node.props as { selectable?: boolean }).selectable === true
        },
        {
          kind: 'boolean',
          path: 'draggable',
          labelKey: 'builderScreen.treeview.draggable',
          helpKey: 'builderScreen.treeview.draggable.help',
          level: 'advanced'
        }
      ]
    },
    {
      labelKey: 'builderScreen.inspector.columnConfiguration',
      level: 'advanced',
      collapsed: true,
      properties: [
        {
          kind: 'custom',
          path: 'columns',
          editor: 'treeColumns',
          labelKey: 'builderScreen.inspector.columns',
          level: 'advanced'
        }
      ]
    },
    buildLayoutGroup()
  ],

  form: { defaultValue: () => [] }
});

// ---------------------------------------------------------------- atributos

export const attributeSetDescriptor = defineDescriptor<'attributeSet'>({
  type: 'attributeSet',
  labelKey: 'builderScreen.palette.attributeSet',
  descriptionKey: 'builderScreen.palette.attributeSet.desc',
  icon: 'tags',
  category: 'data',
  level: 'simple',

  createDefault: context => ({
    props: {
      display: 'cards',
      searchable: true,
      onlyRequiredByDefault: false
    },
    layout: { order: context.order, colSpan: 24, rowSpan: 6 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.groups.essential.title',
      level: 'simple',
      properties: [
        {
          kind: 'text',
          path: 'labelKey',
          labelKey: 'builderScreen.inspector.label',
          level: 'simple'
        },
        {
          // O caminho mais curto: escolher o marketplace e pronto.
          kind: 'enum',
          path: 'profileId',
          labelKey: 'builderScreen.attributes.profile',
          helpKey: 'builderScreen.attributes.profile.help',
          level: 'simple',
          options: buildProfileOptions()
        },
        {
          kind: 'dataPath',
          path: 'dataPath',
          target: 'binding',
          labelKey: 'builderScreen.inspector.dataPath',
          helpKey: 'builderScreen.attributes.dataPathHelp',
          level: 'simple'
        }
      ]
    },
    {
      labelKey: 'builderScreen.attributes.mapping',
      level: 'advanced',
      collapsed: true,
      // Perfil nativo já traz o mapeamento; isto é para marketplace novo.
      visibleWhen: isCustomProfile,
      properties: [
        {
          kind: 'text',
          path: 'sourcePath',
          labelKey: 'builderScreen.attributes.sourcePath',
          helpKey: 'builderScreen.attributes.sourcePath.help',
          level: 'advanced'
        },
        {
          kind: 'enum',
          path: 'nesting',
          labelKey: 'builderScreen.attributes.nesting',
          helpKey: 'builderScreen.attributes.nesting.help',
          level: 'advanced',
          options: [
            { value: 'flat', labelKey: 'builderScreen.attributes.nesting.flat' },
            { value: 'grouped', labelKey: 'builderScreen.attributes.nesting.grouped' },
            { value: 'tree', labelKey: 'builderScreen.attributes.nesting.tree' }
          ]
        },
        {
          // Editor próprio: monta o AttributeFieldMap campo a campo, mostrando
          // ao lado uma amostra do JSON para o usuário conferir o que casou.
          kind: 'custom',
          path: 'fields',
          editor: 'attributeFieldMap',
          labelKey: 'builderScreen.attributes.fieldMap',
          helpKey: 'builderScreen.attributes.fieldMap.help',
          level: 'advanced'
        }
      ]
    },
    {
      labelKey: 'builderScreen.attributes.persistence',
      level: 'advanced',
      collapsed: true,
      properties: [
        {
          kind: 'custom',
          path: 'persistence',
          editor: 'attributePersistence',
          labelKey: 'builderScreen.attributes.persistence',
          helpKey: 'builderScreen.attributes.persistence.help',
          level: 'advanced'
        }
      ]
    },
    {
      labelKey: 'builderScreen.groups.behavior',
      level: 'simple',
      properties: [
        {
          kind: 'enum',
          path: 'display',
          labelKey: 'builderScreen.attributes.display',
          level: 'simple',
          options: [
            { value: 'cards', labelKey: 'builderScreen.attributes.display.cards' },
            { value: 'list', labelKey: 'builderScreen.attributes.display.list' },
            { value: 'tree', labelKey: 'builderScreen.attributes.display.tree' }
          ]
        },
        {
          kind: 'boolean',
          path: 'searchable',
          labelKey: 'builderScreen.inspector.showSearch',
          level: 'simple'
        },
        {
          kind: 'boolean',
          path: 'onlyRequiredByDefault',
          labelKey: 'builderScreen.attributes.onlyRequired',
          helpKey: 'builderScreen.attributes.onlyRequired.help',
          level: 'simple'
        }
      ]
    },
    buildLayoutGroup()
  ],

  form: { defaultValue: () => ({}) }
});

// ---------------------------------------------------------------- componente proprio

export const customComponentDescriptor = defineDescriptor<'customComponent'>({
  type: 'customComponent',
  labelKey: 'builderScreen.palette.customComponent',
  descriptionKey: 'builderScreen.palette.customComponent.desc',
  icon: 'code',
  category: 'display',
  // Só a equipe TemAPI: plugar componente é decisão técnica, não de montagem.
  level: 'advanced',

  createDefault: context => ({
    props: { minHeight: 120 },
    layout: { order: context.order, colSpan: 24, rowSpan: 4 }
  }),

  allowedParents: [...FIELD_PARENTS],

  inspector: [
    {
      labelKey: 'builderScreen.inspector.groups.essential.title',
      level: 'advanced',
      properties: [
        {
          // Componente já compilado e registrado: a forma recomendada.
          kind: 'custom',
          path: 'componentKey',
          editor: 'componentPicker',
          labelKey: 'builderScreen.custom.componentKey',
          helpKey: 'builderScreen.custom.componentKey.help',
          level: 'advanced'
        },
        {
          kind: 'custom',
          path: 'inputs',
          editor: 'keyValueEditor',
          labelKey: 'builderScreen.custom.inputs',
          helpKey: 'builderScreen.custom.inputs.help',
          level: 'advanced'
        },
        {
          kind: 'number',
          path: 'minHeight',
          labelKey: 'builderScreen.custom.minHeight',
          helpKey: 'builderScreen.custom.minHeight.help',
          level: 'advanced',
          min: 0
        }
      ]
    },
    {
      labelKey: 'builderScreen.custom.attachedSource',
      level: 'advanced',
      collapsed: true,
      properties: [
        {
          // Desligado por padrão: executar fonte vindo do banco foi exatamente o
          // problema removido do modo expert. Requer `allowAttachedSource`.
          kind: 'custom',
          path: 'source',
          editor: 'attachedSource',
          labelKey: 'builderScreen.custom.attachedSource',
          helpKey: 'builderScreen.custom.attachedSource.help',
          level: 'advanced'
        }
      ]
    },
    buildLayoutGroup()
  ]
});

export const ADVANCED_DESCRIPTORS: readonly ComponentDescriptor[] = [
  treeViewDescriptor,
  attributeSetDescriptor,
  customComponentDescriptor
];
